import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import { formatRut, RutFormat } from "@fdograph/rut-utilities";

import logoNaranjoFondo from "src/assets/logo/logo_naranjo_fondo.png";
import { formatAsCLP, formatValorPlan } from "src/utils/formatNumber";
import { formatearMonedaCliente } from "src/utils/formatearMonedaCliente";
import { formatearFecha } from "src/utils/formatTime";
import {
  FORMATO_PAGO_LABELS,
  TIPO_DTE_LABELS,
  TIPO_REFERENCIA_LABELS,
  COD_REF_LABELS,
} from "src/utils/dte";
import { BRAND } from "src/theme/brandTokens";

const COLOR_BORDE_RUT = "#C62828";
const COLOR_PLACEHOLDER = "#9e9e9e";
const COLOR_FOOTER_ROJO_BG = "#FFEBEE";
const COLOR_FOOTER_ROJO_TXT = "#D32F2F";

const Placeholder = ({ children }) => (
  <Box component="span" sx={{ color: COLOR_PLACEHOLDER, fontStyle: "italic" }}>
    {children}
  </Box>
);

function formatRutSeguro(rut) {
  if (!rut) return null;
  try {
    return formatRut(rut, RutFormat.DOTS_DASH);
  } catch {
    return rut;
  }
}

function PreviewFacturaBorrador({
  open,
  onClose,
  emisor,
  receptor,
  fecha,
  formatoPago, // numérico 1|2|3 -- opcional
  tipoDocumento = 34, // numérico 33|34|56|61 -- default 34 por compatibilidad
  items = [], // [{ glosa, descripcion, cantidad, precio, moneda, total }]
  referencias = [], // [{ TpoDocRef, FolioRef, FchRef, RazonRef, CodRef? }]
  monedas = [], // [{ moneda, valor }] del reducer; usado solo para la línea de cotización
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const emisorSeguro = emisor || {};
  const receptorSeguro = receptor || {};

  const fechaPreview = fecha instanceof Date ? fecha : new Date();
  const fechaLarga = formatearFecha(fechaPreview, "D [de] MMMM [de] YYYY");

  const rutEmisorFmt = formatRutSeguro(emisorSeguro.rut) || "—";
  const rutReceptorFmt = formatRutSeguro(receptorSeguro.rut);

  const tipoDocumentoLabel =
    TIPO_DTE_LABELS[tipoDocumento] || TIPO_DTE_LABELS[34];
  const formatoPagoLabel = FORMATO_PAGO_LABELS[formatoPago] || null;

  // Total: suma de `item.total` (campo ya en CLP, calculado por el form).
  const total = items.reduce((acc, it) => acc + Number(it?.total || 0), 0);
  const totalFmt =
    total === 0 ? (
      <Box component="span" sx={{ color: COLOR_PLACEHOLDER }}>
        $ 0
      </Box>
    ) : (
      formatAsCLP(total)
    );

  // Etiqueta del bloque de totales: "MONTO EXENTO" para tipo 34, "MONTO NETO"
  // (sin IVA desglosado en este preview) para tipo 33. Para NC/ND mantiene
  // la convención de la factura referenciada.
  const labelMontoBloque = tipoDocumento === 34 ? "MONTO EXENTO" : "MONTO NETO";

  // La columna "Razón Referencia" (CodRef) solo se muestra si al menos una
  // referencia trae CodRef definido (caso NC/ND). FacturarManual hoy no emite
  // NC, así que la columna queda oculta en su uso actual.
  const mostrarColumnaCodRef = referencias.some(
    (r) => r?.CodRef !== undefined && r?.CodRef !== null && r?.CodRef !== "",
  );

  // Tipo de cambio del dia para monedas extranjeras usadas en items.
  // Linea visible solo si al menos una moneda no-PESOS tiene valor disponible
  // en el array `monedas` (formato `[{moneda, valor}]`, igual al provisto por
  // el reducer de monedas en el form).
  const monedasUsadas = Array.from(
    new Set(items.map((it) => it?.moneda).filter((m) => m && m !== "PESOS")),
  );
  const tipoCambioInfo = monedasUsadas
    .map((m) => {
      const entrada = monedas.find((mn) => mn?.moneda === m);
      if (!entrada?.valor) return null;
      return `1 ${formatearMonedaCliente(m)} = ${formatAsCLP(entrada.valor)}`;
    })
    .filter(Boolean);

  const ciudadSII = (emisorSeguro.sucursalSIICiudad || "").toUpperCase();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="preview-factura-borrador-title"
    >
      <DialogTitle
        id="preview-factura-borrador-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 2,
        }}
      >
        <span>Vista previa del borrador</span>
        <IconButton onClick={onClose} size="small" aria-label="cerrar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "#fafafa", p: { xs: 1, md: 3 } }}>
        <Box
          sx={{
            position: "relative",
            bgcolor: "white",
            color: "#333",
            p: { xs: 2, md: 4 },
            border: "1px solid #ddd",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(-30deg)",
              fontSize: { xs: 100, md: 180 },
              fontWeight: 900,
              color: "rgba(255, 149, 0, 0.12)",
              letterSpacing: 20,
              pointerEvents: "none",
              userSelect: "none",
              zIndex: 1,
              whiteSpace: "nowrap",
            }}
          >
            BORRADOR
          </Box>

          <Box sx={{ position: "relative", zIndex: 2 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "flex-start" }}
              spacing={2}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  component="img"
                  src={logoNaranjoFondo}
                  alt="Logo emisor"
                  sx={{
                    width: 90,
                    height: 90,
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    {emisorSeguro.razonSocial || (
                      <Placeholder>[RAZÓN SOCIAL EMISOR]</Placeholder>
                    )}
                  </Typography>
                  {emisorSeguro.giro && (
                    <Typography variant="caption" display="block">
                      {emisorSeguro.giro}
                    </Typography>
                  )}
                  {emisorSeguro.actividadEconomica && (
                    <Typography variant="caption" display="block">
                      {emisorSeguro.actividadEconomica}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block">
                    {emisorSeguro.direccion || "—"}
                    {emisorSeguro.comuna ? `, ${emisorSeguro.comuna}` : ""}
                  </Typography>
                  {emisorSeguro.telefono && (
                    <Typography variant="caption" display="block">
                      Teléfono: {emisorSeguro.telefono}
                    </Typography>
                  )}
                </Box>
              </Stack>

              <Box
                sx={{
                  border: `2px solid ${COLOR_BORDE_RUT}`,
                  p: 1.5,
                  textAlign: "center",
                  minWidth: { md: 260 },
                  alignSelf: { xs: "stretch", md: "flex-start" },
                }}
              >
                <Typography
                  sx={{
                    color: COLOR_BORDE_RUT,
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  R.U.T.: {rutEmisorFmt}
                </Typography>
                <Typography
                  sx={{
                    color: COLOR_BORDE_RUT,
                    fontWeight: 700,
                    fontSize: 13,
                    mt: 0.5,
                  }}
                >
                  {tipoDocumentoLabel}
                </Typography>
                <Typography
                  sx={{
                    color: COLOR_BORDE_RUT,
                    fontWeight: 700,
                    fontSize: 14,
                    mt: 0.5,
                  }}
                >
                  Nº BORRADOR
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ textAlign: "right", mt: 1 }}>
              <Typography variant="caption" sx={{ color: "#666" }}>
                S.I.I. {ciudadSII || <Placeholder>[CIUDAD SII]</Placeholder>}
              </Typography>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <Typography sx={{ fontSize: 13 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Fecha emisión:
                </Box>{" "}
                {fechaLarga}
              </Typography>
              <Typography sx={{ fontSize: 13 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Forma de pago:
                </Box>{" "}
                {formatoPagoLabel || <Placeholder>[PENDIENTE]</Placeholder>}
              </Typography>
            </Stack>

            <Box
              sx={{
                border: "1px solid #999",
                p: 2,
                mt: 2,
                bgcolor: "rgba(255,255,255,0.85)",
              }}
            >
              <Stack spacing={0.5} sx={{ fontSize: 13 }}>
                <Box>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    R.U.T.:{" "}
                  </Box>
                  {rutReceptorFmt || (
                    <Placeholder>[RECEPTOR PENDIENTE]</Placeholder>
                  )}
                </Box>
                <Box>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    SEÑOR(ES):{" "}
                  </Box>
                  {receptorSeguro.razonSocial || (
                    <Placeholder>[RECEPTOR PENDIENTE]</Placeholder>
                  )}
                </Box>
                <Box>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    GIRO:{" "}
                  </Box>
                  {receptorSeguro.giro || (
                    <Placeholder>[GIRO PENDIENTE]</Placeholder>
                  )}
                </Box>
                <Box>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    DIRECCIÓN:{" "}
                  </Box>
                  {receptorSeguro.direccion ? (
                    <>
                      {receptorSeguro.direccion}
                      {receptorSeguro.comuna
                        ? `, ${receptorSeguro.comuna}`
                        : ""}
                    </>
                  ) : (
                    <Placeholder>[DIRECCIÓN PENDIENTE]</Placeholder>
                  )}
                </Box>
              </Stack>
            </Box>

            <Box sx={{ mt: 3 }}>
              <Stack
                direction="row"
                sx={{
                  bgcolor: BRAND.color.primary,
                  color: "white",
                  p: 1,
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                <Box sx={{ flex: 5 }}>Item</Box>
                <Box sx={{ flex: 1, textAlign: "center" }}>Cantidad</Box>
                <Box sx={{ flex: 1.4, textAlign: "right" }}>Precio</Box>
                <Box sx={{ flex: 1.4, textAlign: "right" }}>Subtotal</Box>
              </Stack>
              {items.length === 0 ? (
                <Stack
                  direction="row"
                  sx={{
                    p: 1,
                    borderBottom: "1px solid #eee",
                    fontSize: 13,
                  }}
                >
                  <Box sx={{ flex: 5, pr: 1 }}>
                    <Placeholder>[ITEMS PENDIENTES]</Placeholder>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: "center" }}>—</Box>
                  <Box sx={{ flex: 1.4, textAlign: "right" }}>—</Box>
                  <Box sx={{ flex: 1.4, textAlign: "right" }}>—</Box>
                </Stack>
              ) : (
                items.map((item, index) => {
                  const moneda = item?.moneda || "PESOS";
                  const cantidad = Number(item?.cantidad) || 0;
                  const precioNum = Number(item?.precio) || 0;
                  const subtotalNum = Number(item?.total) || 0;

                  // Precio en moneda original; subtotal siempre en CLP porque `item.total`
                  // ya viene convertido por el form. `formatValorPlan` con `estilo: "v2"`
                  // es el helper canónico para render cliente-facing (mapea PESOS -> CLP,
                  // DOLAR -> USD, etc., y formatea con separadores chilenos).
                  const precioFmt = formatValorPlan(precioNum, moneda, {
                    estilo: "v2",
                  });
                  const subtotalFmt = formatValorPlan(subtotalNum, "PESOS", {
                    estilo: "v2",
                  });

                  return (
                    <Stack
                      key={index}
                      direction="row"
                      sx={{
                        p: 1,
                        borderBottom: "1px solid #eee",
                        fontSize: 13,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ flex: 5, pr: 1 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                          {item?.glosa || (
                            <Placeholder>[GLOSA PENDIENTE]</Placeholder>
                          )}
                        </Typography>
                        {item?.descripcion && (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ color: "#555", whiteSpace: "pre-wrap" }}
                          >
                            {item.descripcion}
                          </Typography>
                        )}
                        {tipoDocumento === 34 && (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ color: BRAND.color.primary, mt: 0.5 }}
                          >
                            No afecto o exento de IVA
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        {cantidad}
                      </Box>
                      <Box sx={{ flex: 1.4, textAlign: "right" }}>
                        {precioFmt}
                      </Box>
                      <Box sx={{ flex: 1.4, textAlign: "right" }}>
                        {subtotalFmt}
                      </Box>
                    </Stack>
                  );
                })
              )}
            </Box>

            {tipoCambioInfo.length > 0 && (
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 1, px: 1, color: "#666", fontStyle: "italic" }}
              >
                Cotización al {formatearFecha(new Date(), "DD/MM/YYYY")}:{" "}
                {tipoCambioInfo.join(" · ")}
              </Typography>
            )}

            {referencias.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  sx={{ fontWeight: 700, fontSize: 13, mb: 1, color: "#444" }}
                >
                  Referencias
                </Typography>
                <Stack
                  direction="row"
                  sx={{
                    bgcolor: "#eeeeee",
                    color: "#333",
                    p: 1,
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  <Box sx={{ flex: 2 }}>Documento</Box>
                  <Box sx={{ flex: 1.2 }}>Folio</Box>
                  <Box sx={{ flex: 1.4 }}>Fecha emisión</Box>
                  {mostrarColumnaCodRef && (
                    <Box sx={{ flex: 2 }}>Razón referencia</Box>
                  )}
                  <Box sx={{ flex: 3 }}>Observaciones</Box>
                </Stack>
                {referencias.map((ref, index) => {
                  const tpoLabel =
                    TIPO_REFERENCIA_LABELS[ref?.TpoDocRef] ||
                    TIPO_DTE_LABELS[ref?.TpoDocRef] ||
                    ref?.TpoDocRef ||
                    "—";

                  const fchRef = ref?.FchRef;
                  let fchRefFmt = "—";
                  if (fchRef) {
                    const fchDate =
                      fchRef instanceof Date
                        ? fchRef
                        : typeof fchRef?.toDate === "function"
                          ? fchRef.toDate()
                          : new Date(fchRef);
                    if (!Number.isNaN(fchDate.getTime())) {
                      try {
                        fchRefFmt = formatearFecha(fchDate, "DD/MM/YYYY");
                      } catch {
                        fchRefFmt = "—";
                      }
                    }
                  }

                  const codRefLabel =
                    ref?.CodRef !== undefined &&
                    ref?.CodRef !== null &&
                    ref?.CodRef !== ""
                      ? COD_REF_LABELS[ref.CodRef] || `Código ${ref.CodRef}`
                      : "—";

                  return (
                    <Stack
                      key={index}
                      direction="row"
                      sx={{
                        p: 1,
                        borderBottom: "1px solid #eee",
                        fontSize: 12,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ flex: 2, pr: 1 }}>{tpoLabel}</Box>
                      <Box sx={{ flex: 1.2, pr: 1 }}>
                        {ref?.FolioRef || <Placeholder>—</Placeholder>}
                      </Box>
                      <Box sx={{ flex: 1.4, pr: 1 }}>{fchRefFmt}</Box>
                      {mostrarColumnaCodRef && (
                        <Box sx={{ flex: 2, pr: 1 }}>{codRefLabel}</Box>
                      )}
                      <Box sx={{ flex: 3, pr: 1, whiteSpace: "pre-wrap" }}>
                        {ref?.RazonRef || <Placeholder>—</Placeholder>}
                      </Box>
                    </Stack>
                  );
                })}
              </Box>
            )}

            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={2}
              sx={{ mt: 4 }}
            >
              <Box
                sx={{
                  border: "2px dashed #999",
                  p: 2,
                  flex: 1,
                  minHeight: 90,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#666", fontStyle: "italic" }}
                >
                  Timbre electrónico · Se generará al emitir el documento
                  tributario
                </Typography>
              </Box>

              <Box sx={{ minWidth: { md: 260 } }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    bgcolor: BRAND.color.primary,
                    color: "white",
                    px: 1.5,
                    py: 0.75,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                    {labelMontoBloque}
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>{totalFmt}</Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    bgcolor: BRAND.color.primary,
                    color: "white",
                    px: 1.5,
                    py: 0.75,
                    mt: 0.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    MONTO TOTAL
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                    {totalFmt}
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            <Box
              sx={{
                mt: 4,
                p: 1.5,
                bgcolor: COLOR_FOOTER_ROJO_BG,
                color: COLOR_FOOTER_ROJO_TXT,
                fontSize: 12,
                borderRadius: 1,
                textAlign: "center",
                fontWeight: 600,
              }}
            >
              ⚠ Este documento es un borrador para validación interna. No tiene
              validez tributaria.
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const areEqual = (prev, next) =>
  prev.open === next.open &&
  prev.tipoDocumento === next.tipoDocumento &&
  prev.formatoPago === next.formatoPago &&
  prev.fecha?.getTime?.() === next.fecha?.getTime?.() &&
  prev.emisor === next.emisor &&
  prev.monedas === next.monedas &&
  JSON.stringify(prev.receptor) === JSON.stringify(next.receptor) &&
  JSON.stringify(prev.items) === JSON.stringify(next.items) &&
  JSON.stringify(prev.referencias) === JSON.stringify(next.referencias);

export default React.memo(PreviewFacturaBorrador, areEqual);
